import { createStore } from '../../../util/mobx/generic-store/store-creator'
import { GenericStoreAsyncMethod } from '../../../util/mobx/generic-store/decorator/async-method.decorator'
import { GenericStore } from '../../../util/mobx/generic-store/generic.store'
import { PortfolioByTypeResponse, CreatePortfolioDto, UpdatePortfolioDto, PortfolioType } from '../model/portfolio'
import { getPortfolio, createPortfolioItem, updateBio, deletePortfolioItem } from '../api/portfolio'
import { notificationStoreInstance } from '../../notification/store/notification.store'
import { localizationStoreInstance } from '../../localization/store/localization.store'

export class PortfolioStore extends GenericStore {
  portfolio?: PortfolioByTypeResponse

  constructor() {
    super('PortfolioStore')
    super.observe(this)
    super.persist({ encrypt: false })
  }

  reset() {
    this.portfolio = undefined
  }

  get hasBio() {
    return Boolean(this.portfolio?.bio)
  }

  get hasCareerGoals() {
    return (this.portfolio?.careerGoals?.data.length || 0) > 0
  }

  get hasInterests() {
    return (this.portfolio?.interests?.data.length || 0) > 0
  }

  get careerGoalsCount() {
    return this.portfolio?.careerGoals?.total || 0
  }

  get interestsCount() {
    return this.portfolio?.interests?.total || 0
  }

  get bio() {
    return this.portfolio?.bio
  }

  get careerGoals() {
    return this.portfolio?.careerGoals?.data || []
  }

  get interests() {
    return this.portfolio?.interests?.data || []
  }

  private initializePortfolioIfNeeded() {
    if (!this.portfolio) {
      this.portfolio = {
        careerGoals: { data: [], total: 0 },
        interests: { data: [], total: 0 },
      }
    }
  }

  private updateItemCount(type: PortfolioType) {
    if (!this.portfolio) {
      return
    }
    
    if (type === PortfolioType.CAREER_GOAL) {
      this.portfolio.careerGoals.total = this.portfolio.careerGoals.data.length
    } else if (type === PortfolioType.INTEREST) {
      this.portfolio.interests.total = this.portfolio.interests.data.length
    }
  }

  @GenericStoreAsyncMethod()
  async handleGetPortfolio(userId?: string) {
    const response = await getPortfolio(userId)
    this.portfolio = response
  }

  @GenericStoreAsyncMethod()
  async handleCreatePortfolioItem(data: CreatePortfolioDto) {
    const response = await createPortfolioItem(data)
    
    this.initializePortfolioIfNeeded()

    if (data.type === PortfolioType.CAREER_GOAL) {
      this.portfolio!.careerGoals.data.push(response)
    } else if (data.type === PortfolioType.INTEREST) {
      this.portfolio!.interests.data.push(response)
    }
    
    this.updateItemCount(data.type)
    
    notificationStoreInstance.enqueueSuccess(
      localizationStoreInstance.translation.pages.profile.portfolioModal.notifications.addedSuccess(),
    )
    
    return response
  }

  @GenericStoreAsyncMethod()
  async handleUpdateBio(data: UpdatePortfolioDto) {
    const response = await updateBio(data)
    
    this.initializePortfolioIfNeeded()
    this.portfolio!.bio = response
    
    notificationStoreInstance.enqueueSuccess(
      localizationStoreInstance.translation.pages.profile.portfolioModal.notifications.updatedSuccess(),
    )
    
    return response
  }

  @GenericStoreAsyncMethod()
  async handleDeletePortfolioItem(portfolioId: string, type: PortfolioType) {
    await deletePortfolioItem(portfolioId)
    
    if (!this.portfolio) {
      return
    }

    if (type === PortfolioType.BIO) {
      this.portfolio.bio = undefined
    } else if (type === PortfolioType.CAREER_GOAL) {
      this.portfolio.careerGoals.data = this.portfolio.careerGoals.data.filter(item => item.id !== portfolioId)
    } else if (type === PortfolioType.INTEREST) {
      this.portfolio.interests.data = this.portfolio.interests.data.filter(item => item.id !== portfolioId)
    }
    
    this.updateItemCount(type)
    
    notificationStoreInstance.enqueueSuccess(
      localizationStoreInstance.translation.pages.profile.portfolioModal.notifications.deletedSuccess(),
    )
  }
}

export const {
  storeInstance: portfolioStoreInstance,
  useStore: usePortfolioStore,
  StoreProvider: PortfolioStoreProvider,
} = createStore(new PortfolioStore())
